import dayjs from "dayjs";

export function validFileExtensions(file, allowedExtensions) {
  if (!file || !Array.isArray(allowedExtensions)) return;
  const extension = file?.name
    ? file?.name?.split(".").pop().toLowerCase()
    : file?.type?.split("/").pop();
  return allowedExtensions.includes(extension);
}

export function formatBytes(bytes, decimals = 2) {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

/**
 *
 * @param {File} file
 * @param {string} headerName - front name
 * @returns
 */
export function renameFile(file, headerName) {
  const randomPart = Math.floor(Math.random() * 100000);
  let newName = `${headerName}${dayjs().format("DDMMYY")}-${randomPart}`;
  return new File([file], newName, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * แปลง File เป็น base64
 * @param {File} file - input file object
 * @returns {Promise<string>} - return base64 string
 */
export async function convertToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

/**
 * แปลง base64 เป็น File และแนบ blob ไว้ใน fileData
 * @param {string} base64 - base64 raw หรือ dataURL
 * @param {string} fileName - ชื่อไฟล์
 * @param {string} mimeType - เช่น image/png, image/jpeg
 * @returns {Promise<File>}
 */
export function base64ToFile(base64, fileName, mimeType) {
  // ตัด prefix data:image/...;base64, ถ้ามี
  const cleanedBase64 = base64.includes(",") ? base64.split(",")[1] : base64;

  // แปลง base64 → binary
  const byteCharacters = atob(cleanedBase64);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    byteArrays.push(new Uint8Array(byteNumbers));
  }

  // สร้าง Blob
  const blob = new Blob(byteArrays, { type: mimeType });

  // สร้าง File จาก Blob
  const file = new File([blob], fileName, {
    type: mimeType,
    lastModified: Date.now(),
  });

  // สร้าง Blob URL
  const blobUrl = URL.createObjectURL(blob);

  // แนบ blob ไว้ใน path fileData
  file.fileData = blobUrl;

  return file;
}

/**
 * แปลง URL ของรูปภาพเป็นไฟล์โดยใช้ Fetch API และแนบ blob ไว้ใน fileData
 * @param {string} imageUrl URL ของรูปภาพ
 * @param {string} [filename="image.jpeg"] ชื่อไฟล์ที่ต้องการสำหรับอ็อบเจ็กต์ File ใหม่
 * @returns {Promise<File>} Promise ที่จะคืนค่าอ็อบเจ็กต์ File
 */
export async function imageUrlToFile(imageUrl, filename = "image.jpeg") {
  try {
    // 1. Fetch the image from the URL
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // 2. แปลง response body เป็น Blob
    const blob = await response.blob();

    // 3. สร้างอ็อบเจ็กต์ File จาก Blob
    // ตัวสร้าง File รับ Array ของส่วน Blob, ชื่อไฟล์, และตัวเลือก (เช่น type)
    const file = new File([blob], filename, {
      // หาก blob.type เป็น "application/octet-stream" ให้ตั้งเป็น "image/jpeg" แทน เนื่องจากบางครั้ง fetch อาจไม่สามารถระบุประเภท MIME ได้ถูกต้อง
      type:
        blob.type === "application/octet-stream" ||
        blob.type === "binary/octet-stream"
          ? "image/jpeg"
          : blob.type,
    });

    // สร้าง Blob URL
    const blobUrl = URL.createObjectURL(blob);

    // แนบ blob ไว้ใน path fileData (สำหรับใช้งานร่วมกับ dropzone)
    file.fileData = blobUrl;

    return file;
  } catch (error) {
    console.error("Error converting image URL to file:", error);
    // You might want to handle CORS issues here, e.g., using a proxy if needed
    throw error;
  }
}

/**
 * แปลง URL ของรูปภาพเป็น base64
 * @param {string} imageUrl - url ของรูปภาพ
 * @returns {Promise<string>} - return base64 string
 * @description ฟังก์ชันนี้จะ fetch รูปภาพจาก URL ที่ให้มา แล้วแปลงเป็น base64 string โดยใช้ FileReader API
 * Note: ฟังก์ชันนี้อาจเจอปัญหา CORS ถ้า URL ของรูปภาพไม่อนุญาตให้เข้าถึงจากโดเมนของคุณ
 * ในกรณีที่เจอปัญหา CORS คุณอาจต้องใช้ proxy server เพื่อดึงรูปภาพมาแทน
 */
export async function imageUrlToBase64(imageUrl, filename = "image.jpeg") {
  try {
    const file = await imageUrlToFile(imageUrl, filename);
    return await convertToBase64(file);
  } catch (error) {
    console.error("Error converting image URL to base64:", error);
    throw error;
  }
}

/**
 *
 * @param {File} file - file object
 * @param {number} maxWidth - maximum width for the compressed image
 * @param {number} quality - quality of the compressed image (0 to 1)
 * @returns {Promise<Blob>} - return compressed image as Blob
 */
export function compressImage(file, maxWidth = 800, quality = 0.8) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      // Calculate new dimensions
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(resolve, file.type, quality);
    };

    img.src = URL.createObjectURL(file);
  });
}
